import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { GetRequest, PostRequest } from "../../services/ApiClientService"
import type { IWhatsAppCrmConversation, IWhatsAppCrmMessage } from "../../Objects/WhatsAppAutomation/WhatsAppCrm"
import type {
  ISendWhatsAppMessagePayload,
  IWhatsAppAgentOverviewDto,
} from "../../Objects/WhatsAppAutomation/WhatsAppAgentOverview"

export const useWhatsAppCrm = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const resolveErrorMessage = (error: unknown) =>
    error instanceof Error ? error.message : t("label_unknown_error_description")

  const isExpectedAgentUnavailable = (error: unknown) => {
    const message = resolveErrorMessage(error).toLowerCase()
    return (
      message.includes("agente de whatsapp") || message.includes("nao respondeu") || message.includes("não respondeu")
    )
  }

  const unwrapList = <T,>(response: unknown): T[] => {
    if (Array.isArray(response)) {
      return response as T[]
    }

    const wrapped = response as { data?: T[] }
    return wrapped.data ?? []
  }

  const getConversations = async (): Promise<IWhatsAppCrmConversation[]> => {
    enableLoad()

    try {
      return unwrapList<IWhatsAppCrmConversation>(
        await GetRequest<IWhatsAppCrmConversation[]>(API_ROUTES.WHATSAPP_AUTOMATION.CRM_CONVERSATIONS),
      )
    } catch (error: unknown) {
      if (isExpectedAgentUnavailable(error)) {
        return []
      }

      setError(t("label_title_erro"), resolveErrorMessage(error))
      return []
    } finally {
      disableLoad()
    }
  }

  const getMessages = async (remotePhone: string): Promise<IWhatsAppCrmMessage[]> => {
    enableLoad()

    try {
      return unwrapList<IWhatsAppCrmMessage>(
        await GetRequest<IWhatsAppCrmMessage[]>(API_ROUTES.WHATSAPP_AUTOMATION.CRM_MESSAGES(remotePhone)),
      )
    } catch (error: unknown) {
      if (isExpectedAgentUnavailable(error)) {
        return []
      }

      setError(t("label_title_erro"), resolveErrorMessage(error))
      return []
    } finally {
      disableLoad()
    }
  }

  const getCrmOverview = async (): Promise<IWhatsAppAgentOverviewDto[]> => {
    try {
      return unwrapList<IWhatsAppAgentOverviewDto>(
        await GetRequest<IWhatsAppAgentOverviewDto[]>(API_ROUTES.WHATSAPP_AUTOMATION.CRM_OVERVIEW),
      )
    } catch (error: unknown) {
      if (isExpectedAgentUnavailable(error)) {
        return []
      }

      setError(t("label_title_erro"), resolveErrorMessage(error))
      return []
    }
  }

  const sendCrmMessage = async (payload: ISendWhatsAppMessagePayload): Promise<void> => {
    enableLoad()

    try {
      await PostRequest<object>(API_ROUTES.WHATSAPP_AUTOMATION.CRM_SEND_MESSAGE, payload)
    } catch (error: unknown) {
      setError(t("label_title_erro"), resolveErrorMessage(error))
      throw error
    } finally {
      disableLoad()
    }
  }

  return { getConversations, getMessages, getCrmOverview, sendCrmMessage }
}
